import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faUser, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FaTiktok, FaFacebookF, FaLinkedinIn, FaGoogle, FaStar, FaSnapchatGhost } from 'react-icons/fa';
import logo from 'assets/images/logo.png';
import { postRequest } from '../../services/api/apiService';
import { isValidEmail } from '../../utils/validators';
import EditorialTheme from '../Home/EditorialTheme';

const ACCENT = '#818CF8';

const PERKS = [
  'Connect 6 ad platforms with one click',
  'AI generates platform-optimised ad copy',
  'Automation rules that run 24/7',
  'Live analytics across every campaign',
  'Free to start — no credit card needed',
];

const PLATFORM_ICONS = [
  { icon: <FaTiktok />, color: '#69C9D0' },
  { icon: <FaFacebookF />, color: '#1877f2' },
  { icon: <FaGoogle />, color: '#4285f4' },
  { icon: <FaLinkedinIn />, color: '#0a66c2' },
  { icon: <FaSnapchatGhost />, color: '#FFFC00' },
  { icon: <span style={{ display: 'inline-flex', width: 13, height: 13, borderRadius: 3, background: '#1862D9', color: '#fff', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900, fontFamily: 'Georgia,serif' }}>t</span>, color: '#1862D9' },
];

const SignUp = () => {
  const [username, setUsername]         = useState('');
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [errors, setErrors]             = useState({ username: false, email: false, password: false });
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const emailInvalid = !trimmedEmail || !isValidEmail(trimmedEmail);
    const newErrors = { username: !username, email: emailInvalid, password: !password };
    setErrors(newErrors);
    if (newErrors.username || newErrors.email || newErrors.password) {
      setErrorMessage(
        emailInvalid && trimmedEmail
          ? 'Please enter a valid email address.'
          : 'All fields are required.'
      );
      return;
    }
    try {
      await postRequest('/api/auth/signup', { username, email: trimmedEmail, password });
      navigate('/login');
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Sign-up failed. Please try again.');
    }
  };

  const handleGoogleSignUp = async (response) => {
    try {
      await postRequest('/api/auth/google-signup', { token: response.credential });
      navigate('/login');
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Google sign-up failed. Please try again.');
    }
  };

  return (
    <div className="obhome" style={{ minHeight: '100vh', display: 'flex' }}>
      <EditorialTheme />
      <style>{`
        .auth-input {
          width: 100%; padding: 12px 14px 12px 42px;
          border: 1.5px solid #E5E6EF; border-radius: 10px;
          font-size: 14px; color: #111; background: #F6F7FB;
          outline: none; box-sizing: border-box;
          transition: border 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .auth-input::placeholder { color: #9CA3AF; }
        .auth-input:focus { border-color: #221C51; background: #fff; box-shadow: 0 0 0 3px rgba(34,28,81,0.09); }
        .auth-input.error { border-color: #EF4444; background: #fff; }
        .auth-submit-btn {
          width: 100%; padding: 14px; border-radius: 999px;
          background: #221C51; color: #fff; font-size: 15px; font-weight: 700;
          border: none; cursor: pointer; transition: all 0.2s ease;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          font-family: 'Space Grotesk', sans-serif;
        }
        .auth-submit-btn:hover { background: #2D2570; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(34,28,81,0.25); }
        @media (max-width: 900px) { .auth-left-panel { display: none !important; } }
      `}</style>

      {/* ── LEFT BRAND PANEL (editorial navy) ── */}
      <div className="auth-left-panel" style={{
        width: '50%',
        background: '#0F0B2D',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '48px 52px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'radial-gradient(ellipse 80% 50% at 30% 0%, rgba(255,255,255,0.05) 0%, transparent 60%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <Link to="/" style={{ display: 'inline-flex', background: '#fff', borderRadius: 8, padding: '6px 10px', marginBottom: 40 }}>
            <img src={logo} alt="OneBotAds" style={{ height: 26, display: 'block' }} />
          </Link>

          <div className="mono-label" style={{ color: 'rgba(255,255,255,0.4)', marginBottom: 18 }}>
            <span style={{ color: ACCENT }}>/</span> Get started
          </div>
          <h2 style={{ fontSize: 'clamp(24px,2.6vw,34px)', fontWeight: 700, color: '#fff', lineHeight: 1.15, marginBottom: 12, letterSpacing: '-0.03em' }}>
            Join 200+ teams scaling{' '}
            <em className="serif-accent" style={{ color: '#A5B4FC' }}>with OneBotAds.</em>
          </h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: 32, maxWidth: 360 }}>
            One platform to create, launch, and automate ads across every channel. Free to start.
          </p>

          {/* Perks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 36 }}>
            {PERKS.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 18, height: 18, borderRadius: 5, background: 'rgba(129,140,248,0.15)', border: '1px solid rgba(129,140,248,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: ACCENT, flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{p}</span>
              </div>
            ))}
          </div>

          {/* Social proof bar */}
          <div style={{ padding: '16px 18px', background: 'rgba(255,255,255,0.04)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.09)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex' }}>
              {['ED','MR','AM','JK'].map((initials, i) => (
                <div key={i} style={{ width: 30, height: 30, borderRadius: '50%', background: '#16122E', border: '2px solid #0F0B2D', marginLeft: i === 0 ? 0 : -8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.7)', fontFamily: "'Space Grotesk',sans-serif" }}>
                  {initials}
                </div>
              ))}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 1, marginBottom: 3 }}>
                {[1,2,3,4,5].map(i => <FaStar key={i} style={{ fontSize: 9, color: ACCENT }} />)}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                <strong style={{ color: '#fff' }}>200+ teams</strong> · avg. rating 4.9/5
              </div>
            </div>
          </div>

          {/* Platform badges */}
          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
            <span className="mono-label" style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>Works with</span>
            {PLATFORM_ICONS.map((p, i) => (
              <div key={i} style={{ width: 30, height: 30, borderRadius: 9, background: '#16122E', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: p.color }}>
                {p.icon}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div style={{ flex: 1, background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 32px' }}>
        <style>{`@media(max-width:900px){.mobile-logo-auth{display:flex!important;}}`}</style>
        <Link to="/" style={{ display: 'none', marginBottom: 28 }} className="mobile-logo-auth">
          <img src={logo} alt="OneBotAds" style={{ height: 32 }} />
        </Link>

        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111', marginBottom: 6, letterSpacing: '-0.03em' }}>Create your account</h1>
            <p style={{ fontSize: 14, color: '#737373' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#4F46E5', fontWeight: 600, textDecoration: 'none' }}>Log in</Link>
            </p>
          </div>

          {errorMessage && (
            <div style={{ padding: '11px 14px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, marginBottom: 20 }}>
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#404040', marginBottom: 7 }}>Username</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faUser} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="text" value={username} onChange={e => { setUsername(e.target.value); setErrors(v => ({ ...v, username: false })); setErrorMessage(''); }} placeholder="Your username" className={`auth-input${errors.username ? ' error' : ''}`} />
              </div>
              {errors.username && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Username is required</p>}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#404040', marginBottom: 7 }}>Email address</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErrors(v => ({ ...v, email: false })); setErrorMessage(''); }} placeholder="you@example.com" className={`auth-input${errors.email ? ' error' : ''}`} />
              </div>
              {errors.email && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Please enter a valid email address</p>}
            </div>

            <div style={{ marginBottom: 22 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#404040', marginBottom: 7 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faLock} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="password" value={password} onChange={e => { setPassword(e.target.value); setErrors(v => ({ ...v, password: false })); setErrorMessage(''); }} placeholder="Min. 8 characters" className={`auth-input${errors.password ? ' error' : ''}`} />
              </div>
              {errors.password && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Password is required</p>}
            </div>

            <button type="submit" className="auth-submit-btn">
              Create account <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
            <div style={{ flex: 1, height: 1, background: '#E5E6EF' }} />
            <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>or</span>
            <div style={{ flex: 1, height: 1, background: '#E5E6EF' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <GoogleLogin onSuccess={handleGoogleSignUp} onError={() => setErrorMessage('Google sign-up failed. Please try again.')} />
          </div>

          <p style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.6 }}>
            By signing up you agree to our{' '}
            <Link to="/terms-of-service" style={{ color: '#4F46E5', fontWeight: 500 }}>Terms</Link>{' '}
            and <Link to="/privacy-policy" style={{ color: '#4F46E5', fontWeight: 500 }}>Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
